import React, { useState, useRef, useEffect } from 'react';
import { Menu, X, ChevronDown, HeartHandshake } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t, lang, setLang, supported } = useLanguage();

  const currentLang = supported.find((l) => l.code === lang) || supported[0];

  const navLinks = [
    { label: t.nav.home, href: '#hero' },
    { label: t.nav.platform, href: '#platform' },
    { label: t.nav.capabilities, href: '#capabilities' },
    { label: t.nav.journey, href: '#journey' },
    { label: t.nav.reasons, href: '#reasons' },
    { label: t.nav.faq, href: '#faq' },
    { label: t.nav.contact, href: '#contact' },
  ];

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-line">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <a href="#hero" className="flex items-center gap-3 group">
            <div
              className="w-10 h-10 bg-teal flex items-center justify-center shadow-soft"
              style={{ borderRadius: '12px' }}
            >
              <HeartHandshake className="w-5 h-5 text-white" aria-hidden />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-lg font-bold text-ink font-heading tracking-tight">
                Digital<span className="text-teal"> Friendo</span>
              </span>
              <span className="text-[10px] sm:text-xs text-muted font-medium tracking-wide">
                {t.brandDomain}
              </span>
            </div>
          </a>

          <nav className="hidden xl:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-muted hover:text-teal transition-colors text-sm font-medium"
              >
                {link.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-2 pl-2.5 pr-3 py-2 bg-surface border border-line text-ink text-sm font-medium hover:border-teal/40 focus:outline-none focus:ring-2 focus:ring-teal/20 transition-colors"
                style={{ borderRadius: '10px' }}
                aria-label="Language"
                aria-expanded={langOpen}
              >
                <ReactCountryFlag
                  countryCode={currentLang.flag}
                  svg
                  style={{ width: '20px', height: '15px', borderRadius: '2px' }}
                  aria-hidden
                />
                <span className="hidden sm:inline">{currentLang.label}</span>
                <ChevronDown className="w-4 h-4 text-muted" aria-hidden />
              </button>
              {langOpen && (
                <div
                  className="absolute right-0 mt-2 w-44 bg-surface shadow-float border border-line py-1 z-50"
                  style={{ borderRadius: '12px' }}
                >
                  {supported.map((l) => (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-sand transition-colors text-sm ${
                        lang === l.code ? 'text-teal font-semibold bg-teal/5' : 'text-ink'
                      }`}
                    >
                      <ReactCountryFlag
                        countryCode={l.flag}
                        svg
                        style={{ width: '20px', height: '15px', borderRadius: '2px' }}
                        aria-hidden
                      />
                      <span>{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="/s"
              className="hidden sm:inline-flex items-center bg-coral hover:bg-coral-light text-white px-5 py-2.5 font-bold text-sm transition-all shadow-soft hover:-translate-y-0.5"
              style={{ borderRadius: '10px' }}
            >
              {t.headerCta}
            </a>
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="xl:hidden text-ink hover:text-teal p-2 border border-line bg-surface"
              style={{ borderRadius: '10px' }}
              aria-expanded={menuOpen}
              aria-label="Menu"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="xl:hidden border-t border-line py-4 pb-5">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="text-ink hover:text-teal transition-colors px-3 py-2.5 text-sm font-medium hover:bg-sand"
                  style={{ borderRadius: '8px' }}
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/s"
                className="mt-2 text-center bg-coral text-white px-6 py-3 font-bold text-sm"
                style={{ borderRadius: '10px' }}
                onClick={() => setMenuOpen(false)}
              >
                {t.headerCta}
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
